import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Form,
    Input,
    Label,
    Row,
    Button,
    Table,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    FormGroup,
} from "reactstrap";
import { getLoyaltyPoints, saveLoyaltyPoints, checkLoyaltyPointsUsage } from "../../components/Api/GmsLoyaltyPoints";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
    hasPagePermissionWithAdmin,
    hasViewOnlyPermission,
    hasEditOrHigherPermission,
    hasDeletePermissionWithAdmin,
    isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '38px',
        borderColor: state.isFocused ? '#556ee6' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
        color: state.isSelected ? 'white' : '#495057',
        '&:hover': {
            backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
        },
    }),
};

// Session management
const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
        userId: authUser.userId || 1,
        userName: authUser.userName || "System",
    };
};

const defaultForm = {
    loyaltyPoints_Id: 0,
    business_Id: null,
    branch_Id: null,
    from_Date: new Date(),
    to_Date: null,
    redeemPoints: "",
    redeemAmount: "",
    earningPoints: "",
    earningAmount: "",
    minimumRedeem: "",
    createdBy: 1,
};

const emptyFilter = {
    business_Id: null,
    branch_Id: null,
    from_Date: null,
    to_Date: null,
};

export default function LoyaltyPointsPage() {
    const [list, setList] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [branches, setBranches] = useState([]);
    const [allBranches, setAllBranches] = useState([]);
    const [form, setForm] = useState(defaultForm);
    const [filter, setFilter] = useState(emptyFilter);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [editLoadingRow, setEditLoadingRow] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [activeTab, setActiveTab] = useState("1");
    const [showFilters, setShowFilters] = useState(false);
    const [isUsedInBilling, setIsUsedInBilling] = useState(false);

    // Check permissions
    const permissionsData = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissionsData, '/GmsLoyaltyPoints');
    const isViewOnly = hasViewOnlyPermission(permissionsData, '/GmsLoyaltyPoints');
    const hasEditOrHigher = hasEditOrHigherPermission(permissionsData, '/GmsLoyaltyPoints');

    useEffect(() => {
        if (hasPermission) {
            loadInitialData();
            if (isViewOnly) {
                setActiveTab("2");
            }
        } else {
            Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
                window.location.href = '/Dashboard';
            });
        }
    }, [hasPermission, isViewOnly]);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const bizData = await getBusinesses({});
            setBusinesses(bizData);

            const branchData = await getBranches({});
            setAllBranches(branchData);

            loadList(emptyFilter);
        } catch (error) {
            console.error("Error loading initial data:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadList = async (filterData = filter) => {
        setLoading(true);
        try {
            const data = await getLoyaltyPoints(filterData);
            setList(data);
            setTotalCount(data.length);
        } catch (error) {
            Swal.fire("Error", "Failed to load loyalty points: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleBusinessChange = (bizId, isFilter = false) => {
        const filteredBranches = allBranches.filter(b => b.business_Id === bizId || b.branch_Buisness_Id === bizId);

        if (isFilter) {
            setFilter(prev => ({ ...prev, business_Id: bizId, branch_Id: null }));
            setBranches(filteredBranches);
        } else {
            setForm(prev => ({ ...prev, business_Id: bizId, branch_Id: null }));
            setBranches(filteredBranches);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.business_Id || !form.branch_Id || !form.from_Date || !form.redeemPoints || !form.redeemAmount || !form.earningPoints || !form.earningAmount) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        setSaveLoading(true);
        try {
            const session = getUserSession();
            const payload = {
                ...form,
                loyaltyPoints_Id: form.loyaltyPoints_Id || 0,
                createdBy: session.userId,
                from_Date: form.from_Date instanceof Date ? form.from_Date.toISOString() : form.from_Date,
                to_Date: form.to_Date ? (form.to_Date instanceof Date ? form.to_Date.toISOString() : form.to_Date) : null,
            };

            const result = await saveLoyaltyPoints(payload);

            if (result.id) {
                Swal.fire("Success", result.message || "Loyalty points saved successfully!", "success");
                handleClear();
                loadList(emptyFilter);
                setActiveTab("2");
            }
        } catch (error) {
            Swal.fire("Error", error.message || "Failed to save loyalty points", "error");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleEdit = async (item) => {
        setEditLoadingRow(item.loyaltyPoints_Id);
        try {
            // Check if configuration is used in billing
            let isUsed = false;
            try {
                const checkResult = await checkLoyaltyPointsUsage(item.loyaltyPoints_Id);
                isUsed = checkResult && checkResult.isUsed;
            } catch (err) {
                console.error("Failed to check usage", err);
            }
            setIsUsedInBilling(isUsed);

            if (isUsed) {
                Swal.fire({
                    title: "Warning",
                    text: "This configuration is already being used in billing. Only the 'To Date' can be modified.",
                    icon: "warning",
                    confirmButtonText: "Understand"
                });
            }

            // Find and set relevant branches
            const filteredBranches = allBranches.filter(b => b.business_Id === item.business_Id || b.branch_Buisness_Id === item.business_Id);
            setBranches(filteredBranches);

            setForm({
                loyaltyPoints_Id: item.loyaltyPoints_Id,
                business_Id: item.business_Id,
                branch_Id: item.branch_Id,
                from_Date: new Date(item.from_Date),
                to_Date: item.to_Date ? new Date(item.to_Date) : null,
                redeemPoints: item.redeemPoints,
                redeemAmount: item.redeemAmount,
                earningPoints: item.earningPoints,
                earningAmount: item.earningAmount,
                minimumRedeem: item.minimumRedeem,
                createdBy: item.createdBy,
            });
            setIsEditing(true);
            setActiveTab("1");
            window.scrollTo(0, 0);
        } catch (error) {
            Swal.fire("Error", "Failed to load data", "error");
        } finally {
            setEditLoadingRow(null);
        }
    };

    const handleClear = () => {
        setForm(defaultForm);
        setIsEditing(false);
        setIsUsedInBilling(false);
    };

    const handleApplyFilter = () => {
        setPage(1);
        loadList(filter);
    };

    const handleClearFilter = () => {
        setFilter(emptyFilter);
        setPage(1);
        loadList(emptyFilter);
    };

    const toggle = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const paginatedData = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return list.slice(startIndex, startIndex + pageSize);
    }, [list, page, pageSize]);

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Loyalty Points Configuration" breadcrumbItem="GMS" />

                <Row>
                    <Col xs={12}>
                        <Card>
                            <CardBody className="p-0">
                                <Nav tabs className="nav-tabs-custom">
                                    {!isViewOnly && (
                                        <NavItem>
                                            <NavLink
                                                style={{
                                                    cursor: "pointer",
                                                    fontWeight: activeTab === "1" ? "bold" : "normal",
                                                    color: activeTab === "1" ? "#556ee6" : "#74788d",
                                                    borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                                                    padding: "15px 25px",
                                                    fontSize: "16px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px"
                                                }}
                                                className={activeTab === "1" ? "active" : ""}
                                                onClick={() => toggle("1")}
                                            >
                                                <i className="bx bx-plus-circle"></i>
                                                <span>Loyalty Points Form</span>
                                            </NavLink>
                                        </NavItem>
                                    )}
                                    <NavItem>
                                        <NavLink
                                            style={{
                                                cursor: "pointer",
                                                fontWeight: activeTab === "2" ? "bold" : "normal",
                                                color: activeTab === "2" ? "#556ee6" : "#74788d",
                                                borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                                                padding: "15px 25px",
                                                fontSize: "16px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px"
                                            }}
                                            className={activeTab === "2" ? "active" : ""}
                                            onClick={() => toggle("2")}
                                        >
                                            <i className="bx bx-list-ul"></i>
                                            <span>Loyalty Points List</span>
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                <TabContent activeTab={activeTab} className="p-4">
                                    {!isViewOnly && (
                                        <TabPane tabId="1">
                                            <Form onSubmit={handleSubmit}>
                                                <Row className="mb-4">
                                                    <Col md={4}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Company <span className="text-danger">*</span></Label>
                                                            <Select
                                                                value={businesses.find(b => b.business_Id === form.business_Id) ? {
                                                                    value: form.business_Id,
                                                                    label: businesses.find(b => b.business_Id === form.business_Id).business_NameEn
                                                                } : null}
                                                                onChange={(opt) => handleBusinessChange(opt ? opt.value : null)}
                                                                options={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn }))}
                                                                styles={selectStyles}
                                                                isClearable={!isUsedInBilling}
                                                                placeholder="Select Company"
                                                                isDisabled={isUsedInBilling && isEditing}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Branch <span className="text-danger">*</span></Label>
                                                            <Select
                                                                value={branches.find(b => b.branch_Id === form.branch_Id) ? {
                                                                    value: form.branch_Id,
                                                                    label: branches.find(b => b.branch_Id === form.branch_Id).branch_NameEn
                                                                } : null}
                                                                onChange={(opt) => setForm(prev => ({ ...prev, branch_Id: opt ? opt.value : null }))}
                                                                options={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))}
                                                                styles={selectStyles}
                                                                isClearable
                                                                placeholder="Select Branch"
                                                                isDisabled={!form.business_Id || (isUsedInBilling && isEditing)}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Effective From <span className="text-danger">*</span></Label>
                                                            <Flatpickr
                                                                className="form-control"
                                                                options={{ dateFormat: "Y-m-d" }}
                                                                value={form.from_Date}
                                                                onChange={(date) => setForm(prev => ({ ...prev, from_Date: date[0] }))}
                                                                disabled={isUsedInBilling && isEditing}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col md={4}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Effective To (Optional)</Label>
                                                            <Flatpickr
                                                                className="form-control"
                                                                options={{ dateFormat: "Y-m-d" }}
                                                                value={form.to_Date}
                                                                onChange={(date) => setForm(prev => ({ ...prev, to_Date: date[0] }))}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>

                                                <h5 className="mb-3 text-primary">Earning Configuration</h5>
                                                <Row className="mb-4">
                                                    <Col md={6}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Earning Points <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={form.earningPoints}
                                                                onChange={(e) => setForm(prev => ({ ...prev, earningPoints: e.target.value }))}
                                                                placeholder="e.g. 0.05"
                                                                disabled={isUsedInBilling && isEditing}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Per Amount Spent <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={form.earningAmount}
                                                                onChange={(e) => setForm(prev => ({ ...prev, earningAmount: e.target.value }))}
                                                                placeholder="e.g. 1"
                                                                disabled={isUsedInBilling && isEditing}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>

                                                <h5 className="mb-3 text-primary">Redemption Configuration</h5>
                                                <Row className="mb-4">
                                                    <Col md={4}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Redeem Points <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={form.redeemPoints}
                                                                onChange={(e) => setForm(prev => ({ ...prev, redeemPoints: e.target.value }))}
                                                                placeholder="e.g. 250"
                                                                disabled={isUsedInBilling && isEditing}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Redeem Amount <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={form.redeemAmount}
                                                                onChange={(e) => setForm(prev => ({ ...prev, redeemAmount: e.target.value }))}
                                                                placeholder="e.g. 25"
                                                                disabled={isUsedInBilling && isEditing}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup className="mb-3">
                                                            <Label>Min. Redeem Points <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={form.minimumRedeem}
                                                                onChange={(e) => setForm(prev => ({ ...prev, minimumRedeem: e.target.value }))}
                                                                placeholder="e.g. 250"
                                                                disabled={isUsedInBilling && isEditing}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <div className="d-flex gap-2 mt-3">
                                                    <Button color="primary" type="submit" disabled={saveLoading}>
                                                        {saveLoading && <ClipLoader size={16} color="#fff" className="me-2" />}
                                                        {isEditing ? "Update Configuration" : "Save Configuration"}
                                                    </Button>
                                                    <Button color="secondary" type="button" onClick={handleClear}>Clear</Button>
                                                </div>
                                            </Form>
                                        </TabPane>
                                    )}

                                    <TabPane tabId="2">
                                        <Card>
                                            <CardHeader>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="card-title mb-0">Filter Loyalty Points</h5>
                                                    <Button
                                                        style={{
                                                            backgroundColor: '#556ee6',
                                                            borderColor: '#556ee6',
                                                            color: 'white',
                                                            borderRadius: '6px',
                                                            padding: '8px 16px',
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            border: 'none',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => setShowFilters(!showFilters)}
                                                    >
                                                        {showFilters ? "Hide Filters" : "Show Filters"}
                                                    </Button>
                                                </div>
                                            </CardHeader>
                                            {showFilters && (
                                                <CardBody>
                                                    <Row>
                                                        <Col md={3}>
                                                            <FormGroup>
                                                                <Label>Company</Label>
                                                                <Select
                                                                    value={businesses.find(b => b.business_Id === filter.business_Id) ? {
                                                                        value: filter.business_Id,
                                                                        label: businesses.find(b => b.business_Id === filter.business_Id).business_NameEn
                                                                    } : null}
                                                                    onChange={(opt) => handleBusinessChange(opt ? opt.value : null, true)}
                                                                    options={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn }))}
                                                                    styles={selectStyles}
                                                                    isClearable
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={3}>
                                                            <FormGroup>
                                                                <Label>Branch</Label>
                                                                <Select
                                                                    value={branches.find(b => b.branch_Id === filter.branch_Id) ? {
                                                                        value: filter.branch_Id,
                                                                        label: branches.find(b => b.branch_Id === filter.branch_Id).branch_NameEn
                                                                    } : null}
                                                                    onChange={(opt) => setFilter(prev => ({ ...prev, branch_Id: opt ? opt.value : null }))}
                                                                    options={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))}
                                                                    styles={selectStyles}
                                                                    isClearable
                                                                    isDisabled={!filter.business_Id}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={6} className="d-flex align-items-end mb-3">
                                                            <Button color="primary" className="me-2" onClick={handleApplyFilter}>Apply</Button>
                                                            <Button color="secondary" onClick={handleClearFilter}>Reset</Button>
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            )}
                                        </Card>

                                        {loading ? (
                                            <div className="text-center py-5">
                                                <ClipLoader size={40} color="#556ee6" />
                                                <p className="mt-2">Loading data...</p>
                                            </div>
                                        ) : (
                                            <div className="table-responsive">
                                                <Table bordered hover className="align-middle">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Sr.No</th>
                                                            <th>Company</th>
                                                            <th>Branch</th>
                                                            <th>From Date</th>
                                                            <th>To Date</th>
                                                            <th>Earn Pts</th>
                                                            <th>Earn Amt</th>
                                                            <th>Redeem Pts</th>
                                                            <th>Redeem Amt</th>
                                                            <th>Min Red</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedData.length > 0 ? paginatedData.map((item, index) => (
                                                            <tr key={item.loyaltyPoints_Id}>
                                                                <td>{(page - 1) * pageSize + index + 1}</td>
                                                                <td>{item.business_NameEn}</td>
                                                                <td>{item.branch_NameEn}</td>
                                                                <td>{item.from_Date ? new Date(item.from_Date).toLocaleDateString('en-GB') : "-"}</td>
                                                                <td>{item.to_Date ? new Date(item.to_Date).toLocaleDateString('en-GB') : "-"}</td>
                                                                <td>{item.earningPoints}</td>
                                                                <td>{item.earningAmount}</td>
                                                                <td>{item.redeemPoints}</td>
                                                                <td>{item.redeemAmount}</td>
                                                                <td>{item.minimumRedeem}</td>
                                                                <td>
                                                                    {hasEditOrHigher && (
                                                                        <Button
                                                                            color="soft-primary"
                                                                            size="sm"
                                                                            onClick={() => handleEdit(item)}
                                                                            disabled={editLoadingRow === item.loyaltyPoints_Id}
                                                                        >
                                                                            {editLoadingRow === item.loyaltyPoints_Id ? (
                                                                                <ClipLoader size={14} />
                                                                            ) : (
                                                                                <SquarePen size={16} />
                                                                            )}
                                                                        </Button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )) : (
                                                            <tr>
                                                                <td colSpan="8" className="text-center text-muted">No configurations found</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
